// "Efetivar": after the meals are made, their ingredients leave the stock. Each comes with what the plan says and can be
// changed when more or less was used; one click settles them all, with "Desfazer". An item whose recipe unit does not
// convert to its own ("1 un" of a fish counted in kg) starts empty: the user says how much was used.
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography,
} from '@mui/material';
import * as api from '../plannerApi';
import * as T from '../types';
import { useRun, useUndoable } from '../common';
import { quantity, shortMeal, unitShort } from '../labels';

/** The meals in `slotIds` (all still to settle when omitted); `onClose(true)` once settled or undone. */
export function SettleDialog({ slotIds, onClose }: { slotIds?: T.Guid[]; onClose: (changed: boolean) => void }) {
  const run = useRun();
  const offerUndo = useUndoable();
  const [settlement, setSettlement] = useState<T.MealSettlement | null>(null);
  const [used, setUsed] = useState<Record<T.Guid, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    run(() => api.getMealSettlement(slotIds)).then(loaded => {
      if (!loaded) return onClose(false);
      setSettlement(loaded);
      const start: Record<T.Guid, string> = {};
      loaded.items.forEach(i => { start[i.catalogItemPublicId] = i.unconverted.length > 0 ? '' : String(i.planned); });
      setUsed(start);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = (item: T.SettlementItem) => {
    const text = (used[item.catalogItemPublicId] ?? '').trim();
    return text === '' ? NaN : Number(text.replace(',', '.'));
  };
  const invalid = settlement?.items.some(i => !(value(i) >= 0)) ?? true;

  const settle = async () => {
    if (!settlement) return;
    const body: T.SettleMealsRequest = {
      slotPublicIds: settlement.meals.map(m => m.slotPublicId),
      items: settlement.items.map(i => ({ catalogItemPublicId: i.catalogItemPublicId, quantity: value(i) })),
    };
    setBusy(true);
    const done = await run(() => api.settleMeals(body));
    setBusy(false);
    if (!done) return;
    const count = body.slotPublicIds.length;
    offerUndo(count === 1 ? '1 refeição efetivada' : `${count} refeições efetivadas`,
      () => api.settleMeals({ ...body, undo: true }), () => onClose(true), { severity: 'success', success: 'Voltou ao stock' });
    onClose(true);
  };

  return (
    <Dialog open onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Efetivar refeições</DialogTitle>
      <DialogContent dividers>
        {settlement === null ? (
          <Typography variant="body2" color="text.secondary">A carregar…</Typography>
        ) : (
          <>
            {settlement.meals.map(m => (
              <Typography key={m.slotPublicId} variant="body2" sx={{ fontWeight: 600 }}>{shortMeal(m.date, m.mealType)} · {m.title}</Typography>
            ))}
            {settlement.meals.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Não há refeições por efetivar.</Typography>
            ) : settlement.items.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Nenhum ingrediente destas refeições é contado no stock: só ficam confirmadas.
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 1 }}>
                  Ajusta o que gastaste a mais ou a menos; o resto sai como planeado.
                </Typography>
                {settlement.items.map(item => (
                  <Box key={item.catalogItemPublicId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.unconverted.length > 0
                          ? `receita ${item.unconverted.map(u => quantity(u.quantity, u.unit))
                            .concat(item.planned > 0 ? [quantity(item.planned, item.unit)] : []).join(' + ')} · diz quanto gastaste`
                          : `planeado ${quantity(item.planned, item.unit)}${value(item) !== item.planned ? ' · alterado' : ''}`}
                        {' · em casa '}{quantity(item.stock, item.unit)}
                      </Typography>
                    </Box>
                    <TextField
                      size="small" sx={{ width: 130 }} value={used[item.catalogItemPublicId] ?? ''}
                      error={(used[item.catalogItemPublicId] ?? '').trim() !== '' && !(value(item) >= 0)}
                      placeholder={item.unconverted.length > 0 ? 'quanto?' : undefined}
                      onChange={e => setUsed({ ...used, [item.catalogItemPublicId]: e.target.value })}
                      slotProps={{
                        input: { endAdornment: <InputAdornment position="end">{unitShort(item.unit)}</InputAdornment> },
                        htmlInput: { inputMode: 'decimal', 'aria-label': `${item.name} usado` },
                      }}
                    />
                  </Box>
                ))}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button variant="contained" disabled={busy || invalid || !settlement || settlement.meals.length === 0} onClick={settle}>
          Efetivar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
